import {
  compose, equals, isEmpty, length, map as rMap, pipe, propOr, sum, values,
} from 'ramda';
import { combineEpics, ofType } from 'redux-observable';
import { concat, of } from 'rxjs';
import {
  distinctUntilChanged, filter, flatMap, map, pluck, skipUntil, take,
} from 'rxjs/operators';

import { actions as bootstrapActions, selectors as bootstrapSelectors } from '@ezugi/bootstrap';
import {
  NO_MORE_BETS, PLACE_YOUR_BETS, BACK, LAY,
} from '@ezugi/constants';

import { BACK_LAY_BETS, BL_TOTALS, LAY_BETS } from '../../../../constants/BackAndLay/betTypes';
import { myBetsActions } from '../../../actions/myBets';
import { totalsActions } from '../../../actions/totals';
import { keypadActions } from '../../../reducers/keypad/BackAndLay';
import {
  payoutsValuesSelector,
  playerABackSelector,
  playerALaySelector,
  playerBBackSelector,
  playerBLaySelector,
  prevStakesSelector,
  totalPlayerABackSelector,
  totalPlayerALaySelector,
  totalPlayerBBackSelector,
  totalPlayerBLaySelector,
} from '../../../selectors/BackAndLay';
import { currentBetsSelector } from '../../../selectors/bets';
import { hasReachedCardsLimitSelector } from '../../../selectors/cards';
import { getTotalBet } from '../../bets/utils';
import { createPlaceBetRequestPayload } from '../../bets/utils/request';
import { createBackAndLayBetsList } from './request';
import { validateBet } from './validations';

const { prevStakes } = keypadActions;
const { totals } = totalsActions;
const {
  roundActions: { round },
  betActions: { bet, totalBet, history },
} = bootstrapActions;
const { payoutsSelector } = bootstrapSelectors;
const { PLAYER_A_LAY, PLAYER_B_LAY } = BACK_LAY_BETS;

/* BETS EPICS */

const betRequestEpic = (action$, state$) => action$.pipe(
  ofType(round.set),
  pluck('payload', 'roundStatus'),
  distinctUntilChanged(),
  filter(equals(NO_MORE_BETS)),
  skipUntil(
    action$.pipe(
      ofType(round.set),
      pluck('payload', 'roundStatus'),
      distinctUntilChanged(),
      filter(equals(PLACE_YOUR_BETS)),
      take(1)
    )
  ),
  map(() => createPlaceBetRequestPayload(state$.value, createBackAndLayBetsList)),
  filter(length),
  flatMap((_actions) => concat(...rMap(of, _actions), of(bet.success())))
);

const placeBetEpic = (action$, state$) => action$.pipe(
  ofType(bet.request),
  pluck('payload'),
  map((bets) => validateBet(bets, state$.value)),
  flatMap(({ valid, bet: _bet, ...result }) => {
    const actions = (valid && _bet && !isEmpty(_bet)) ? [ of(bet.set(_bet)) ] : [];
    actions.push(...rMap(of, result.actions));
    if (!isEmpty(actions)) actions.push(of(myBetsActions.myBets.cache()));

    return concat(...actions);
  })
);

const betResetEpic = (action$, state$) => action$.pipe(
  ofType(round.set),
  pluck('payload'),
  filter(({ roundStatus }) => roundStatus === PLACE_YOUR_BETS && hasReachedCardsLimitSelector(state$.value)),
  flatMap(() => concat(of(bet.reset()), of(history.reset())))
);

const totalBetEpic = (action$, state$) => action$.pipe(
  ofType(bet.set),
  map(() => {
    const currentBets = currentBetsSelector(state$.value);
    const payouts = payoutsSelector(state$.value);

    const adjustLayStake = (bets) => Object.entries(bets).map(([ k, v ]) => {
      const odd = payouts[k].value;
      const stake = v.value;
      // for placed lay bets calculate the bet amount according to the stake
      return Object.values(LAY_BETS).includes(k)
        ? { ...v, value: +(odd * stake).toFixed(2) }
        : v;
    });

    const totalBetValue = compose(
      getTotalBet,
      adjustLayStake
    )(currentBets);

    return totalBet.set({ value: totalBetValue });
  })
);

const totalsForAllBetsEpic = (action$, state$) => action$.pipe(
  ofType(bet.success),
  flatMap(() => {
    const payoutsValues = payoutsValuesSelector(state$.value);

    const calcBackTotalValue = (betTotalSelector, currBetValueSelector) => (
      betTotalSelector(state$.value) + propOr(0, 'value', currBetValueSelector(state$.value))
    );
    const calcLayTotalValue = (odd, betTotalSelector, currBetValueSelector) => {
      const betTotal = betTotalSelector(state$.value);
      const currBetValue = propOr(0, 'value', currBetValueSelector(state$.value));
      return +(betTotal + (odd * currBetValue)).toFixed(2);
    };

    const totalsObj = {
      // TOTALS FOR BACK BETS
      [BL_TOTALS.TOTAL_PLAYER_A_BACK]: calcBackTotalValue(totalPlayerABackSelector, playerABackSelector),
      [BL_TOTALS.TOTAL_PLAYER_B_BACK]: calcBackTotalValue(totalPlayerBBackSelector, playerBBackSelector),
      // TOTALS FOR LAY BETS
      /* eslint-disable max-len */
      [BL_TOTALS.TOTAL_PLAYER_A_LAY]: calcLayTotalValue(payoutsValues[PLAYER_A_LAY], totalPlayerALaySelector, playerALaySelector),
      [BL_TOTALS.TOTAL_PLAYER_B_LAY]: calcLayTotalValue(payoutsValues[PLAYER_B_LAY], totalPlayerBLaySelector, playerBLaySelector),
      /* eslint-enable max-len */
    };

    return of(
      totals.set(totalsObj),
      bet.clear(),
      totalBet.set({ value: pipe(values, sum)(totalsObj) }),
    );
  }),
);

const prevStakesEpic = (action$, state$) => action$.pipe(
  ofType(bet.set),
  pluck('payload'),
  flatMap(({ type, value }) => {
    const prevStakesSaved = !isEmpty(prevStakesSelector(state$.value));
    const newStakes = prevStakesSaved
      ? { [Object.values(LAY_BETS).includes(type) ? LAY : BACK]: value }
      : { [BACK]: value, [LAY]: value };
    return of(prevStakes.set(newStakes));
  })
);

export default combineEpics(
  placeBetEpic,
  totalBetEpic,
  betResetEpic,
  betRequestEpic,
  totalsForAllBetsEpic,
  prevStakesEpic,
);
